/**
 * This code has been adapted from [arcgis-pbf-parser] ([https://github.com/rowanwins/arcgis-pbf-parser])
 * Modifications have been made for use in this project.
 */
import { ArcGISRequestError } from "@esri/arcgis-rest-request";
import { readFeatureCollectionPBuffer } from "./PbfFeatureCollectionV2.js";
import Pbf from "pbf";

export interface EsriGeoJSONFeatureCollection {
  type: "FeatureCollection";
  // optional coordinate reference system that may not be necessary
  features: any[];
  properties?: {
    exceededTransferLimit?: boolean;
  };
}

export default function pbfToGeoJSON(
  arrayBuffer: ArrayBuffer | Uint8Array | Buffer
): EsriGeoJSONFeatureCollection {
  // return decoded pbf as geojson structure
  const decoded = decode(arrayBuffer);
  return decoded;
}

export function decode(featureCollectionBuffer: any) {
  let decodedObject;
  try {
    decodedObject = readFeatureCollectionPBuffer(
      new Pbf(featureCollectionBuffer)
    );
  } catch (error) {
    /* istanbul ignore next --@preserve */
    throw new Error(`Could not parse arcgis-pbf buffer: ${error}`);
  }
  if (!decodedObject.queryResult) {
    throw new Error("Could not parse arcgis-pbf buffer: Missing queryResult.");
  }
  const featureResult = decodedObject.queryResult.featureResult;
  const transform = featureResult.transform;
  const geometryType = featureResult.geometryType;
  const hasZ = featureResult.hasZ === true;
  const objectIdField = featureResult.objectIdFieldName;

  const out: EsriGeoJSONFeatureCollection = {
    type: "FeatureCollection",
    features: []
  };

  out.properties = {
    exceededTransferLimit: featureResult.exceededTransferLimit
  };

  const geometryParser = getGeometryParser(geometryType);

  // decode the field keynames
  const fields = featureResult.fields;
  fields.forEach((field: any) => {
    field.keyName = getKeyName(field);
  });

  // throw error if hasM is true
  if (featureResult.hasM) {
    throw new ArcGISRequestError(
      "M values are not supported for GeoJSON.",
      422
    );
  }

  // throw error if objectIdField does not exist in the fields
  if (fields.every((field: any) => field.name !== objectIdField)) {
    throw new ArcGISRequestError(
      `objectIdField '${objectIdField}' was not found.`,
      400
    );
  }

  const featureLen = featureResult.features.length;
  for (let index = 0; index < featureLen; index++) {
    const f = featureResult.features[index];
    out.features.push({
      type: "Feature",
      // deliberately not setting feature id here to improve performance
      properties: collectAttributes(fields, f.attributes),
      geometry: (f.geometry && geometryParser(f, transform, hasZ)) || null
    });
  }
  // set feature ids after the fact here
  // collect attributes must be called first to ensure properties are populated
  out.features.forEach((feature, idx) => {
    feature.id = feature.properties[objectIdField];
  });

  return out;
}

// * @property {number} esriGeometryTypePoint=0 esriGeometryTypePoint value
// * @property {number} esriGeometryTypeMultipoint=1 esriGeometryTypeMultipoint value
// * @property {number} esriGeometryTypePolyline=2 esriGeometryTypePolyline value
// * @property {number} esriGeometryTypePolygon=3 esriGeometryTypePolygon value
// * @property {number} esriGeometryTypeMultipatch=4 esriGeometryTypeMultipatch value
// * @property {number} esriGeometryTypeNone=127 esriGeometryTypeNone value
function getGeometryParser(featureType: number) {
  switch (featureType) {
    case 3:
      return createPolygon;
    case 2:
      return createLine;
    case 0:
      return createPoint;
    /* istanbul ignore next --@preserve */
    default:
      throw new ArcGISRequestError("Geometry type not supported.", 501);
  }
}

function getCoordinateDimensions(hasZ: boolean) {
  return hasZ ? 3 : 2;
}

function createPoint(f: any, transform: any, hasZ: boolean) {
  const dimensions = getCoordinateDimensions(hasZ);
  const p = {
    type: "Point",
    coordinates: transformTuple(
      f.geometry.coords.slice(0, dimensions),
      transform
    )
  };
  return p;
}

function createLine(f: any, transform: any, hasZ: boolean) {
  let l = null;
  const lengths = f.geometry.lengths.length;
  const dimensions = getCoordinateDimensions(hasZ);

  /* istanbul ignore else if --@preserve */
  if (lengths === 1) {
    l = {
      type: "LineString",
      coordinates: createLinearRing(
        f.geometry.coords,
        transform,
        0,
        f.geometry.lengths[0] * dimensions,
        hasZ
      )
    };
  } else if (lengths > 1) {
    l = {
      type: "MultiLineString",
      coordinates: []
    };
    let startPoint = 0;
    for (let index = 0; index < lengths; index++) {
      const stopPoint = startPoint + f.geometry.lengths[index] * dimensions;
      const line = createLinearRing(
        f.geometry.coords,
        transform,
        startPoint,
        stopPoint,
        hasZ
      );
      l.coordinates.push(line);
      startPoint = stopPoint;
    }
  }
  return l;
}

function createPolygon(f: any, transform: any, hasZ: boolean) {
  const lengths = f.geometry.lengths.length;
  const dimensions = getCoordinateDimensions(hasZ);

  const p = {
    type: "Polygon",
    coordinates: [] as any[]
  };

  if (lengths === 1) {
    p.coordinates.push(
      createLinearRing(
        f.geometry.coords,
        transform,
        0,
        f.geometry.lengths[0] * dimensions,
        hasZ
      )
    );
  } else {
    p.type = "MultiPolygon";

    let startPoint = 0;
    for (let index = 0; index < lengths; index++) {
      const stopPoint = startPoint + f.geometry.lengths[index] * dimensions;
      const ring = createLinearRing(
        f.geometry.coords,
        transform,
        startPoint,
        stopPoint,
        hasZ
      );

      // Check if the ring is clockwise, if so it's an outer ring
      // If it's counter-clockwise its a hole and so push it to the prev outer ring
      // This is perhaps a bit naive
      // see https://github.com/terraformer-js/terraformer/blob/master/packages/arcgis/src/geojson.js
      // for a fuller example of doing this
      /* istanbul ignore else if --@preserve */
      if (ringIsClockwise(ring)) {
        p.coordinates.push([ring]);
      } else if (p.coordinates.length > 0) {
        p.coordinates[p.coordinates.length - 1].push(ring);
      }
      startPoint = stopPoint;
    }
  }
  return p;
}

function ringIsClockwise(ringToTest: any) {
  let total = 0;
  let i = 0;
  const rLength = ringToTest.length;
  let pt1 = ringToTest[i];
  let pt2;
  for (i; i < rLength - 1; i++) {
    pt2 = ringToTest[i + 1];
    total += (pt2[0] - pt1[0]) * (pt2[1] + pt1[1]);
    pt1 = pt2;
  }
  return total >= 0;
}

function createLinearRing(
  arr: number[],
  transform: any,
  startPoint: number,
  stopPoint: number,
  hasZ: boolean
) {
  const out = [] as any[];
  /* istanbul ignore if --@preserve */
  if (arr.length === 0) return out;

  const dimensions = getCoordinateDimensions(hasZ);

  const initialX = arr[startPoint];
  const initialY = arr[startPoint + 1];
  const initialCoords = hasZ
    ? [initialX, initialY, arr[startPoint + 2]]
    : [initialX, initialY];
  out.push(transformTuple(initialCoords, transform));

  let prevX = initialX;
  let prevY = initialY;
  for (let i = startPoint + dimensions; i < stopPoint; i = i + dimensions) {
    const x = difference(prevX, arr[i]);
    const y = difference(prevY, arr[i + 1]);
    const coords = hasZ ? [x, y, arr[i + 2]] : [x, y];
    const transformed = transformTuple(coords, transform);
    out.push(transformed);
    prevX = x;
    prevY = y;
  }
  return out;
}

function collectAttributes(fields: any, featureAttributes: any) {
  const out = {} as { [key: string]: any };
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (featureAttributes[i][featureAttributes[i].value_type] !== undefined)
      out[f.name] = featureAttributes[i][featureAttributes[i].value_type];
    else out[f.name] = null;
  }
  return out;
}

function getKeyName(fields: any) {
  switch (fields.fieldType) {
    case 1:
      return "sintValue";
    case 2:
      return "floatValue";
    case 3:
      return "doubleValue";
    case 4:
      return "stringValue";
    /* istanbul ignore next --@preserve */
    case 5:
      return "sint64Value";
    case 6:
      return "uintValue";
    default:
      return null;
  }
}

/* istanbul ignore next --@preserve */
function transformTuple(coords: any, transform: any) {
  let x = coords[0];
  let y = coords[1];

  let z = coords.length > 2 ? coords[2] : undefined;
  const scale = transform?.scale || {};
  const translate = transform?.translate || {};

  const xScale = scale.xScale ?? 1;
  const yScale = scale.yScale ?? 1;
  const zScale = scale.zScale ?? 1;
  const xTranslate = translate.xTranslate ?? 0;
  const yTranslate = translate.yTranslate ?? 0;
  const zTranslate = translate.zTranslate ?? 0;

  if (transform.scale) {
    x *= xScale;
    y *= -yScale;
    if (undefined !== z) {
      z *= zScale;
    }
  }
  if (transform.translate) {
    x += xTranslate;
    y += yTranslate;
    if (undefined !== z) {
      z += zTranslate;
    }
  }
  const ret = [x, y];
  if (undefined !== z) {
    ret.push(z);
  }
  return ret;
}

function difference(a: any, b: any) {
  return a + b;
}
